import {
  COMPOSE_EMAIL_DELIVERIES,
  COMPOSE_EMAIL_LIMITS,
  COMPOSE_EMAIL_PARENT_TYPES,
  type ComposeEmailDelivery,
  type ComposeEmailInput,
  type ComposeEmailParent,
  type ComposeEmailParentType,
  type ComposeEmailRecipient,
} from '@realadvisor/common-types';
import { encodeGlobalId } from '@realadvisor/transform/global-id';
import { getUserDisplayName } from '@realadvisor/transform/user-display-name';
import { z } from 'zod';

import type {
  EmailTemplateSelectFragment,
  GetComposeEmailDraftQuery,
  GetComposeEmailDraftQueryVariables,
} from '@graphql/__generated__/graphql';
import type { RichEditorValue } from '@modules/_shared/components/email-editor/htmlValue';
import type {
  EmailFormFile,
  EmailFormRecipient,
  EmailRecipient,
  EmailTemplateSuggestion,
  ReplyToMessage,
} from '@modules/_shared/components/email-form/types';

// Pure logic behind useComposeEmailDraft: validation of the agent's
// `compose_email` input, the lookup variables, and turning the lookup result
// into composer props. Kept free of React/Apollo so it runs under
// `pnpm test:node`.

export type ComposeEmailParentLink = {
  type: ComposeEmailParentType;
  id: string;
  globalId: string;
  label: string;
  path: string;
};

export type ComposeEmailTypeLabels = Record<ComposeEmailParentType, string>;

// Shape-only, like the chat server's `z.guid()`: every id is resolved against
// Hasura anyway. Lower-cased so ids compare equal to the ones Hasura returns.
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const idSchema = z
  .string()
  .regex(UUID_RE, 'Invalid id (expected a uuid)')
  .transform(id => id.toLowerCase());

const recipientSchema = z.object({
  email: z.string().max(254).email(),
  name: z.string().min(1).max(200).optional(),
  userId: idSchema.optional(),
  buyerLeadId: idSchema.optional(),
});

const recipientListSchema = z
  .array(recipientSchema)
  .max(COMPOSE_EMAIL_LIMITS.maxRecipients);

const RECIPIENT_LISTS = ['to', 'cc', 'bcc'] as const;

// Mirrors the chat server's schema (crm-chat-api/tools/client.ts) rule for
// rule, so a draft the model was allowed to make always opens here.
export const composeEmailInputSchema = z
  .object({
    delivery: z.enum(COMPOSE_EMAIL_DELIVERIES).optional(),
    // The single-delivery cap (maxRecipients) is checked in superRefine.
    to: z
      .array(recipientSchema)
      .min(1)
      .max(COMPOSE_EMAIL_LIMITS.maxIndividualRecipients),
    cc: recipientListSchema.optional(),
    bcc: recipientListSchema.optional(),
    subject: z.string().min(1).max(COMPOSE_EMAIL_LIMITS.maxSubject).optional(),
    bodyHtml: z.string().min(1).max(COMPOSE_EMAIL_LIMITS.maxBody).optional(),
    templateId: idSchema.optional(),
    parents: z
      .array(
        z.object({ type: z.enum(COMPOSE_EMAIL_PARENT_TYPES), id: idSchema }),
      )
      .max(COMPOSE_EMAIL_LIMITS.maxParents)
      .optional(),
    replyToEmailMessageId: idSchema.optional(),
    fileIds: z.array(idSchema).max(COMPOSE_EMAIL_LIMITS.maxFiles).optional(),
  })
  .superRefine((input, ctx) => {
    const issue = (message: string, ...path: Array<string | number>) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, message, path });
    if (input.delivery === 'individual') {
      for (const list of ['cc', 'bcc'] as const) {
        if ((input[list]?.length ?? 0) > 0) {
          issue(
            `${list} is not allowed with delivery "individual" (each ` +
              'recipient gets their own email); put everyone in "to", or ' +
              `use delivery "single" to ${list} people`,
            list,
          );
        }
      }
      if (input.replyToEmailMessageId != null) {
        issue(
          'replyToEmailMessageId is not allowed with delivery "individual"; ' +
            'reply with delivery "single"',
          'replyToEmailMessageId',
        );
      }
      // The campaign matches each sent message back to its recipient by address.
      const addresses = new Set<string>();
      input.to.forEach((recipient, index) => {
        const address = recipient.email.trim().toLowerCase();
        if (addresses.has(address)) {
          issue(
            `Duplicate recipient ${recipient.email}: with delivery ` +
              '"individual" each address may appear only once in "to"',
            'to',
            index,
            'email',
          );
        }
        addresses.add(address);
      });
    } else {
      if (input.to.length > COMPOSE_EMAIL_LIMITS.maxRecipients) {
        issue(
          `"to" accepts at most ${COMPOSE_EMAIL_LIMITS.maxRecipients} ` +
            'recipients with delivery "single"; to email each recipient ' +
            'separately use delivery "individual" (max ' +
            `${COMPOSE_EMAIL_LIMITS.maxIndividualRecipients})`,
          'to',
        );
      }
      if ((input.parents?.length ?? 0) === 0) {
        issue(
          'parents needs at least one CRM record with delivery "single"',
          'parents',
        );
      }
      for (const list of RECIPIENT_LISTS) {
        input[list]?.forEach((recipient, index) => {
          if (recipient.buyerLeadId != null) {
            issue(
              'buyerLeadId is only allowed with delivery "individual"; with ' +
                '"single", link the buyer lead through parents instead',
              list,
              index,
              'buyerLeadId',
            );
          }
        });
      }
    }
    if (input.templateId != null) {
      if (input.bodyHtml != null) {
        issue('Omit bodyHtml when templateId is set', 'bodyHtml');
      }
      // The composer applies a template only to a new email, never a reply.
      if (input.replyToEmailMessageId != null) {
        issue(
          'templateId cannot be used with replyToEmailMessageId',
          'templateId',
        );
      }
    } else {
      // A reply without a subject keeps the composer's "Re: <original>".
      if (input.subject == null && input.replyToEmailMessageId == null) {
        issue(
          'subject is required unless templateId or replyToEmailMessageId is set',
          'subject',
        );
      }
      if (input.bodyHtml == null) {
        issue('bodyHtml is required unless templateId is set', 'bodyHtml');
      }
    }
    const parents = new Set<string>();
    for (const parent of input.parents ?? []) {
      const key = `${parent.type}:${parent.id}`;
      if (parents.has(key)) {
        issue(`Duplicate parent ${parent.type} ${parent.id}`, 'parents');
      }
      parents.add(key);
    }
  }) satisfies z.ZodType<ComposeEmailInput>;

export type ParsedComposeEmailInput =
  | { ok: true; input: ComposeEmailInput }
  | { ok: false; error: string };

export const parseComposeEmailInput = (
  rawInput: unknown,
): ParsedComposeEmailInput => {
  const result = composeEmailInputSchema.safeParse(rawInput);
  if (result.success) {
    return { ok: true, input: result.data };
  }
  const issues = result.error.issues.map(
    issue =>
      `- ${issue.path.length > 0 ? issue.path.join('.') : 'input'}: ${
        issue.message
      }`,
  );
  return {
    ok: false,
    error: `Invalid compose_email input:\n${issues.join('\n')}`,
  };
};

// The types `sendUnitaryEmail` decodes from `activityParentIds`.
const GLOBAL_ID_TYPES: Record<ComposeEmailParentType, string> = {
  lead: 'Lead',
  buyer_lead: 'BuyerLead',
  enquiry: 'Enquiry',
  lot: 'Lot',
  contact: 'User',
};

const PATH_PREFIXES: Record<ComposeEmailParentType, string> = {
  lead: '/leads',
  buyer_lead: '/v2/buyer-leads',
  enquiry: '/enquiries',
  lot: '/listings',
  contact: '/users',
};

export const composeEmailParentGlobalId = ({
  type,
  id,
}: ComposeEmailParent): string => encodeGlobalId(GLOBAL_ID_TYPES[type], id);

export const composeEmailParentPath = ({
  type,
  id,
}: ComposeEmailParent): string => `${PATH_PREFIXES[type]}/${id}`;

const unique = (values: string[]): string[] => [...new Set(values)];

const allRecipients = (input: ComposeEmailInput): ComposeEmailRecipient[] => [
  ...input.to,
  ...(input.cc ?? []),
  ...(input.bcc ?? []),
];

const parentIds = (
  input: ComposeEmailInput | null,
  type: ComposeEmailParentType,
): string[] =>
  unique(
    (input?.parents ?? [])
      .filter(parent => parent.type === type)
      .map(parent => parent.id),
  );

export const composeEmailDraftVariables = (
  input: ComposeEmailInput | null,
): GetComposeEmailDraftQueryVariables => {
  const recipients = input == null ? [] : allRecipients(input);
  return {
    userIds: unique(
      recipients.flatMap(recipient =>
        recipient.userId == null ? [] : [recipient.userId],
      ),
    ),
    emails: unique(
      recipients
        .filter(recipient => recipient.userId == null)
        .map(recipient => recipient.email.toLowerCase()),
    ),
    fileIds: unique(input?.fileIds ?? []),
    templateIds: input?.templateId == null ? [] : [input.templateId],
    replyToIds:
      input?.replyToEmailMessageId == null ? [] : [input.replyToEmailMessageId],
    leadIds: parentIds(input, 'lead'),
    buyerLeadIds: unique([
      ...parentIds(input, 'buyer_lead'),
      ...recipients.flatMap(recipient =>
        recipient.buyerLeadId == null ? [] : [recipient.buyerLeadId],
      ),
    ]),
    enquiryIds: parentIds(input, 'enquiry'),
    lotIds: parentIds(input, 'lot'),
    contactIds: parentIds(input, 'contact'),
  };
};

/** Record ids that give template variables their lead/listing/buyer context. */
export const composeEmailSuggestionAnchors = (input: ComposeEmailInput) => {
  const first = (type: ComposeEmailParentType) =>
    (input.parents ?? []).find(parent => parent.type === type)?.id ?? null;
  return {
    leadId: first('lead'),
    listingId: first('lot'),
    buyerLeadId: first('buyer_lead'),
    enquiryId: first('enquiry'),
  };
};

type DraftUser = GetComposeEmailDraftQuery['recipientsById'][number];
type NamedRecord = {
  first_name?: string | null;
  last_name?: string | null;
  full_name?: string | null;
};

// Names only: getUserDisplayName would fall back to an email's local part.
const displayName = (record: NamedRecord | null | undefined): string =>
  record == null
    ? ''
    : getUserDisplayName(
        {
          first_name: record.first_name,
          last_name: record.last_name,
          full_name: record.full_name,
        },
        { fallbackToId: false },
      );

const joinLabel = (...parts: Array<string | null | undefined>): string =>
  parts.filter(part => part != null && part.trim() !== '').join(' · ');

const sameAddress = (a: string, b: string): boolean =>
  a.toLowerCase() === b.toLowerCase();

const ownsAddress = (user: DraftUser, email: string): boolean =>
  user.emails.some(entry => sameAddress(entry.email, email));

/** undefined when the record is missing or invisible; '' when it has no name. */
const parentLabel = (
  data: GetComposeEmailDraftQuery,
  { type, id }: ComposeEmailParent,
): string | undefined => {
  switch (type) {
    case 'lead': {
      const lead = data.leads.find(row => row.id === id);
      return lead == null
        ? undefined
        : displayName(lead.contact) || displayName(lead);
    }
    case 'buyer_lead': {
      const buyerLead = data.buyer_leads.find(row => row.id === id);
      return buyerLead == null
        ? undefined
        : joinLabel(displayName(buyerLead.user), buyerLead.lot.title);
    }
    case 'enquiry': {
      const enquiry = data.enquiries.find(row => row.id === id);
      return enquiry == null
        ? undefined
        : joinLabel(
            displayName(enquiry.user) || displayName(enquiry),
            enquiry.lot?.title,
          );
    }
    case 'lot': {
      const lot = data.lots.find(row => row.id === id);
      return lot == null ? undefined : lot.title ?? '';
    }
    case 'contact': {
      const contact = data.contacts.find(row => row.id === id);
      return contact == null ? undefined : displayName(contact);
    }
  }
};

const parseEmailRecipients = (value: unknown): EmailRecipient[] | null => {
  if (!Array.isArray(value)) {
    return null;
  }
  return value.flatMap((entry: unknown) => {
    if (entry == null || typeof entry !== 'object') {
      return [];
    }
    const email = 'email' in entry ? entry.email : null;
    const name = 'name' in entry ? entry.name : null;
    if (typeof email !== 'string' || email === '') {
      return [];
    }
    return [{ email, name: typeof name === 'string' ? name : null }];
  });
};

export type ResolvedComposeEmailDraft =
  | {
      ok: true;
      delivery: ComposeEmailDelivery;
      toRecipients: EmailFormRecipient[];
      ccRecipients: EmailFormRecipient[];
      bccRecipients: EmailFormRecipient[];
      files: EmailFormFile[];
      template: EmailTemplateSelectFragment | null;
      replyToMessage: ReplyToMessage | null;
      parentLinks: ComposeEmailParentLink[];
    }
  | { ok: false; error: string };

export type ComposeEmailFormProps = {
  initialRecipients: EmailFormRecipient[];
  initialCcRecipients: EmailFormRecipient[];
  initialBccRecipients: EmailFormRecipient[];
  initialSubject?: string;
  initialBodyValue?: RichEditorValue | null;
  initialTemplate?: EmailTemplateSelectFragment | null;
  initialFiles: EmailFormFile[];
  parentGlobalIds: string[];
  mode: 'compose' | 'reply';
  replyToMessage?: ReplyToMessage;
  // Template drafts only: the composer applies the template on mount, so its
  // variables must already be loaded or the subject keeps the raw keys.
  templateSuggestions?: EmailTemplateSuggestion[];
  /** delivery 'individual': EmailForm sends one email per recipient as a campaign. */
  campaignMode: boolean;
};

export const composeEmailFormProps = ({
  input,
  resolved,
  bodyValue,
  templateSuggestions,
}: {
  input: ComposeEmailInput;
  resolved: Extract<ResolvedComposeEmailDraft, { ok: true }>;
  bodyValue: RichEditorValue | null;
  templateSuggestions: EmailTemplateSuggestion[] | null;
}): ComposeEmailFormProps => ({
  initialRecipients: resolved.toRecipients,
  initialCcRecipients: resolved.ccRecipients,
  initialBccRecipients: resolved.bccRecipients,
  // Absent on a reply: the composer then opens with "Re: <original>".
  ...(input.subject == null ? {} : { initialSubject: input.subject }),
  initialBodyValue: bodyValue,
  initialTemplate: resolved.template,
  initialFiles: resolved.files,
  parentGlobalIds: resolved.parentLinks.map(link => link.globalId),
  mode: resolved.replyToMessage != null ? 'reply' : 'compose',
  replyToMessage: resolved.replyToMessage ?? undefined,
  ...(templateSuggestions == null ? {} : { templateSuggestions }),
  campaignMode: resolved.delivery === 'individual',
});

/** The subject EmailForm's computeInitialSubject shows for a reply. */
export const composeEmailReplySubject = (
  subject: string | null | undefined,
): string => {
  if (!subject) {
    return '';
  }
  return subject.startsWith('Re:') ? subject : `Re: ${subject}`;
};

/** The subject the composer opens with (template subjects before variables). */
export const composeEmailDraftSubject = (
  input: ComposeEmailInput,
  {
    template,
    replyToMessage,
  }: {
    template: EmailTemplateSelectFragment | null;
    replyToMessage: ReplyToMessage | null;
  },
): string => {
  if (input.subject != null) {
    return input.subject;
  }
  if (replyToMessage != null) {
    return composeEmailReplySubject(replyToMessage.subject);
  }
  return template?.subject_plaintext ?? template?.subject ?? '';
};

// The composer's To/Cc/Bcc fields are user pickers keyed by users.id (as are
// existing callers' initial recipients), so every address must resolve to a
// CRM user the current user can see. An address given with a userId must be
// one of that user's addresses: the picker shows the user, not the address.
export const resolveComposeEmailDraft = (
  input: ComposeEmailInput,
  data: GetComposeEmailDraftQuery,
  typeLabels: ComposeEmailTypeLabels,
): ResolvedComposeEmailDraft => {
  const problems: string[] = [];
  const notVisible = (what: string, id: string) =>
    problems.push(`${what} ${id} was not found or is not visible to you`);

  const usersById = new Map(data.recipientsById.map(user => [user.id, user]));
  const findUser = (recipient: ComposeEmailRecipient): DraftUser | null => {
    if (recipient.userId != null) {
      const user = usersById.get(recipient.userId);
      if (user == null) {
        notVisible('User', `${recipient.userId} (${recipient.email})`);
        return null;
      }
      if (!ownsAddress(user, recipient.email)) {
        problems.push(
          `${recipient.email} is not an email address of user ${recipient.userId}`,
        );
        return null;
      }
      return user;
    }
    const candidates = data.recipientsByEmail.filter(user =>
      ownsAddress(user, recipient.email),
    );
    const user =
      candidates.find(candidate =>
        candidate.emails.some(
          entry => entry.primary && sameAddress(entry.email, recipient.email),
        ),
      ) ?? candidates[0];
    if (user == null) {
      problems.push(
        `No CRM contact or user visible to you has the email address ${recipient.email}; the composer only sends to CRM contacts, so add the address to a contact first`,
      );
      return null;
    }
    return user;
  };

  const buyerLeadsById = new Map(data.buyer_leads.map(row => [row.id, row]));
  // The campaign logs the email on the buyer lead and advances its stage, so
  // it must be the recipient's own lead. Same owner as getEffectiveUser: the
  // MLS view stands in when RLS hides `user`.
  const buyerLeadProblem = (
    buyerLeadId: string,
    recipient: ComposeEmailRecipient,
    user: DraftUser | null,
  ): string | null => {
    const buyerLead = buyerLeadsById.get(buyerLeadId);
    if (buyerLead == null) {
      return `Buyer lead ${buyerLeadId} (${recipient.email}) was not found or is not visible to you`;
    }
    const ownerId = buyerLead.user?.id ?? buyerLead.mls_user?.id ?? null;
    if (ownerId == null) {
      return `Buyer lead ${buyerLeadId} has no buyer visible to you, so it cannot be linked to ${recipient.email}`;
    }
    if (user != null && ownerId.toLowerCase() !== user.id.toLowerCase()) {
      return `Buyer lead ${buyerLeadId} belongs to user ${ownerId}, not to ${recipient.email} (user ${user.id})`;
    }
    return null;
  };

  const addressById = new Map<string, string>();
  const resolveList = (
    recipients: ComposeEmailRecipient[] | undefined,
  ): EmailFormRecipient[] =>
    (recipients ?? []).flatMap(recipient => {
      const user = findUser(recipient);
      const { buyerLeadId } = recipient;
      const buyerLeadError =
        buyerLeadId == null
          ? null
          : buyerLeadProblem(buyerLeadId, recipient, user);
      if (buyerLeadError != null) {
        problems.push(buyerLeadError);
      }
      if (user == null || buyerLeadError != null) {
        return [];
      }
      const previous = addressById.get(user.id);
      if (previous != null) {
        if (!sameAddress(previous, recipient.email)) {
          problems.push(
            `User ${user.id} is listed with two addresses (${previous}, ${recipient.email}); use only one`,
          );
        }
        return [];
      }
      addressById.set(user.id, recipient.email);
      return [
        {
          id: user.id,
          email: recipient.email,
          name: displayName(user) || undefined,
          ...(buyerLeadId == null
            ? {}
            : {
                buyerLeadId: composeEmailParentGlobalId({
                  type: 'buyer_lead',
                  id: buyerLeadId,
                }),
              }),
        },
      ];
    });

  const toRecipients = resolveList(input.to);
  const ccRecipients = resolveList(input.cc);
  const bccRecipients = resolveList(input.bcc);

  const filesById = new Map(data.files.map(file => [file.id, file]));
  const files = unique(input.fileIds ?? []).flatMap((id): EmailFormFile[] => {
    const file = filesById.get(id);
    if (file == null) {
      notVisible('File', id);
      return [];
    }
    return [
      {
        id: file.id,
        name: file.name,
        url: file.url,
        size: file.size,
        mimetype: file.mimetype ?? '',
      },
    ];
  });

  let template: EmailTemplateSelectFragment | null = null;
  if (input.templateId != null) {
    template =
      data.email_templates.find(row => row.id === input.templateId) ?? null;
    if (template == null) {
      notVisible('Email template', input.templateId);
    }
  }

  let replyToMessage: ReplyToMessage | null = null;
  if (input.replyToEmailMessageId != null) {
    const id = input.replyToEmailMessageId;
    const message = data.email_messages_aggregate.nodes.find(
      row => row.id === id,
    );
    if (message == null) {
      notVisible('Email message', id);
    } else if (message.message_id == null || message.message_id === '') {
      problems.push(
        `Email message ${id} has no provider message id, so it cannot be answered in its thread`,
      );
    } else {
      replyToMessage = {
        messageId: message.message_id,
        createdBy: message.created_by ?? null,
        subject: message.subject ?? null,
        from: parseEmailRecipients(message.from),
        cc: parseEmailRecipients(message.cc),
        bcc: parseEmailRecipients(message.bcc),
        date: typeof message.date === 'string' ? message.date : null,
      };
    }
  }

  const parentLinks = (input.parents ?? []).flatMap(
    (parent): ComposeEmailParentLink[] => {
      const label = parentLabel(data, parent);
      if (label == null) {
        notVisible(`Parent ${parent.type}`, parent.id);
        return [];
      }
      return [
        {
          type: parent.type,
          id: parent.id,
          globalId: composeEmailParentGlobalId(parent),
          label: label || typeLabels[parent.type],
          path: composeEmailParentPath(parent),
        },
      ];
    },
  );

  if (problems.length > 0) {
    return {
      ok: false,
      error: `Cannot prepare this email:\n${problems
        .map(problem => `- ${problem}`)
        .join('\n')}`,
    };
  }
  return {
    ok: true,
    delivery: input.delivery ?? 'single',
    toRecipients,
    ccRecipients,
    bccRecipients,
    files,
    template,
    replyToMessage,
    parentLinks,
  };
};
